import { useEffect, useState } from "react";
import useInputObject from "../../hooks/useInputObject";
import {
  searchSalesRecordByNo,
  upsertSalesRecord,
  upsertStock,
} from "../../http/api/SalesRecordAPI";
import { getLoginUserInfo } from "../../utils/loginUser";
import {
  getHyphenedDate,
  isToday
} from "../../utils/utils";
import { isNullEmpty } from "../../utils/validation";
import SALES_RECORD_TYPE from "../../utils/variable";
import {
  SALES_DETAIL_ROW_COUNT,
  SALES_RESULTS_ROW_COUNT,
  detailIsEmpty,
  getMaxSalesDetailId,
  getMaxSalesDetailNo,
  getMaxSalesResultsId,
  initialSalesDetails,
  initialSalesRecord,
  initialSalesResultses,
  resultsIsEmpty
} from "./util/InitialSalesRecord";
import validateSalesRecord from "./validSalesRecord";

const DEFAULT_LOCAL_ORIGIN_ID = 59;

function generateNewSalesRecord({
  salesRecordNo = "",
  arrivalDate,
  deliveryDate,
  salesRecordTypeCd,
}) {
  salesRecordTypeCd =
    salesRecordTypeCd == SALES_RECORD_TYPE.LOCAL
      ? SALES_RECORD_TYPE.LOCAL
      : SALES_RECORD_TYPE.TRAVEL;

  //新規原票の売立日は本日以降のみ
  let solvedArrivalDate = arrivalDate || initialSalesRecord.arrivalDate;
  let todayDate = getHyphenedDate();
  if (new Date(solvedArrivalDate) < new Date(todayDate)) {
    solvedArrivalDate = initialSalesRecord.arrivalDate;
  }

  return {
    salesRecordInfo: {
      ...initialSalesRecord,
      isNewInvoice: true,
      salesRecordNo,
      salesRecordTypeCd,
      arrivalDate: solvedArrivalDate,
      deliveryDate: deliveryDate || initialSalesRecord.deliveryDate,
      originId:
        salesRecordTypeCd == SALES_RECORD_TYPE.LOCAL
          ? DEFAULT_LOCAL_ORIGIN_ID
          : null,
      createdId: getLoginUserInfo().userId,
      updatedId: getLoginUserInfo().userId,
      updateHistoryProps: []
    },
    listSalesDetailInfo: initialSalesDetails(1, 1),
    listSalesResultsInfo: initialSalesResultses(1),
  };
}

function fillLocalInvoice(response) {
  let newResponse = { ...response };

  newResponse.salesRecordInfo.createdId = getLoginUserInfo().userId;
  newResponse.salesRecordInfo.updatedId = getLoginUserInfo().userId;

  const detailRowCount = newResponse.listSalesDetailInfo.length;
  const missingDetailCount = SALES_DETAIL_ROW_COUNT - detailRowCount;
  const maxSalesDetailId = getMaxSalesDetailId(newResponse.listSalesDetailInfo);
  const maxSalesDetailNo = getMaxSalesDetailNo(newResponse.listSalesDetailInfo);

  if (missingDetailCount > 0) {
    let newSalesDetails = initialSalesDetails(
      maxSalesDetailId + 1,
      maxSalesDetailNo + 1,
      missingDetailCount
    );
    newResponse.listSalesDetailInfo = [
      ...newResponse.listSalesDetailInfo,
      ...newSalesDetails,
    ];
  }

  const resultsRowCount = newResponse.listSalesResultsInfo.length;
  const missingResultsCount = SALES_RESULTS_ROW_COUNT - resultsRowCount;
  const maxSalesResultsId = getMaxSalesResultsId(
    newResponse.listSalesResultsInfo
  );

  if (missingResultsCount > 0) {
    let newSalesResultses = initialSalesResultses(
      maxSalesResultsId + 1,
      missingResultsCount
    );
    newResponse.listSalesResultsInfo = [
      ...newResponse.listSalesResultsInfo,
      ...newSalesResultses,
    ];
  }
  return newResponse;
}

function fillTravelInvoice(response) {
  let newResponse = { ...response };
  const salesRecordType = newResponse.salesRecordInfo.salesRecordTypeCd;

  newResponse.salesRecordInfo.createdId = getLoginUserInfo().userId;
  newResponse.salesRecordInfo.updatedId = getLoginUserInfo().userId;

  //#region 旅物は仕入値入力されていない場合は売り立て日当日に設定する
  const hasPurchacePrice = newResponse.listSalesDetailInfo.every(
    (detailInfo) => detailInfo.isNew || Boolean(detailInfo.purchacePrice)
  );
  if (!hasPurchacePrice && salesRecordType === SALES_RECORD_TYPE.TRAVEL) {
    newResponse.salesRecordInfo.arrivalDate = getHyphenedDate();
    newResponse.salesRecordInfo.deliveryDate = getHyphenedDate();
  }
  //#endregion

  const detailRowCount = newResponse.listSalesDetailInfo.length;
  const missingDetailCount = SALES_DETAIL_ROW_COUNT - detailRowCount;
  const maxSalesDetailId = getMaxSalesDetailId(newResponse.listSalesDetailInfo);
  const maxSalesDetailNo = getMaxSalesDetailNo(newResponse.listSalesDetailInfo);

  if (missingDetailCount > 0) {
    let newSalesDetails = initialSalesDetails(
      maxSalesDetailId + 1,
      maxSalesDetailNo + 1,
      missingDetailCount
    );
    newResponse.listSalesDetailInfo = [
      ...newResponse.listSalesDetailInfo,
      ...newSalesDetails,
    ];
  }

  const resultsRowCount = newResponse.listSalesResultsInfo.length;
  const missingResultsCount = SALES_RESULTS_ROW_COUNT - resultsRowCount;
  const maxSalesResultsId = getMaxSalesResultsId(
    newResponse.listSalesResultsInfo
  );

  if (missingResultsCount > 0) {
    let newSalesResultses = initialSalesResultses(
      maxSalesResultsId + 1,
      missingResultsCount
    );
    newResponse.listSalesResultsInfo = [
      ...newResponse.listSalesResultsInfo,
      ...newSalesResultses,
    ];
  }
  return newResponse;
}
const initialInvoice = generateNewSalesRecord({});

const useSalesRecordAPI = () => {
  const [isLoading, setLoading] = useState(false);
  const invoice = useInputObject(initialInvoice);
  const insertedInvoice = useInputObject(initialInvoice);
  const renderState = useInputObject();
  useEffect(() => {
    if (renderState.Value == "StartLoading") {
      setLoading(true);
      return;
    }
    if (!renderState.Value["salesRecord"]) return;
    const salesDetailCount = invoice.Value.listSalesDetailInfo.length;
    const salesResultsCount = invoice.Value.listSalesResultsInfo.length;
    for (let i = 0; i < salesDetailCount; i++) {
      if (!renderState.Value[`detail-${i}`]) return;
    }
    for (let i = 0; i < salesResultsCount; i++) {
      if (!renderState.Value[`results-${i}`]) return;
    }
    setLoading(false);
    console.timeEnd("getSalesRecordByNo Execution Time"); // End the timer
  }, [renderState.Value, invoice.Value]);

  async function getSalesRecordByNo(salesRecord, salesRecordType) {
    try {
      console.log("\n---Get SalesRecord---");
      console.time("getSalesRecordByNo Execution Time"); // Start the timer
      renderState.setValue("StartLoading");
      let response = await searchSalesRecordByNo(
        salesRecord.Value.salesRecordNo
      );
      const responseRecord = response.listSalesRecordInvoice[0];
      let filledInvoice = { salesRecordInfo: { isNewInvoice: false } };

      if (salesRecordType == SALES_RECORD_TYPE.STOCK) {
        filledInvoice = fillStockInvoice(responseRecord);
      } else if (salesRecordType == SALES_RECORD_TYPE.TRAVEL) {
        filledInvoice = fillTravelInvoice(responseRecord);
      } else {
        filledInvoice = fillLocalInvoice(responseRecord);
      }

      invoice.setValue({
        ...filledInvoice,
      });

      return filledInvoice;
    } catch (e) {
      invoice.setValue(
        generateNewSalesRecord({
          salesRecordNo: salesRecord.Value.salesRecordNo,
          arrivalDate: salesRecord.Value.arrivalDate,
          salesRecordTypeCd: salesRecordType,
        })
      );

      return null;
    } finally {
    }
  }

  function fillStockInvoice(invoice) {
    let { listSalesDetailInfo, listSalesResultsInfo } = invoice;

    const todaySalesResultsInfo = listSalesResultsInfo.filter((result) =>
      isToday(result.salesResultsDate)
    );
    const otherSalesResultsInfo = listSalesResultsInfo.filter(
      (result) => !isToday(result.salesResultsDate)
    );

    invoice.salesRecordInfo.createdId = getLoginUserInfo().userId;
    invoice.salesRecordInfo.updatedId = getLoginUserInfo().userId;

    const soldQuantities = otherSalesResultsInfo.reduce((acc, result) => {
      acc[result.salesDetailId] =
        (acc[result.salesDetailId] || 0) + result.quantity;
      return acc;
    }, {});

    const newListDetails = listSalesDetailInfo
      .map((detail) => {
        const soldQuantity = soldQuantities[detail.salesDetailId] || 0;
        return {
          ...detail,
          quantity: detail.quantity - soldQuantity,
        };
      })
      .filter((detail) => detail.quantity > 0);

    let maxSalesResultsId = Math.max(
      ...invoice.listSalesResultsInfo.map((item) => item.salesResultsId)
    );

    if (maxSalesResultsId === -Infinity) {
      maxSalesResultsId = 0;
    }

    const cloneSalesResults = initialSalesResultses(maxSalesResultsId + 1);

    let newListSalesResults = [...todaySalesResultsInfo, ...cloneSalesResults];

    //最初の販売実績の販売明細Noを自動設定する
    //明細がない場合は設定しない
    if (newListDetails.length > 0) {
      //すでに１行目の実績の明細No設定されているなら処理しない
      if (isNullEmpty(newListSalesResults[0].salesDetailNo)) {
        //１行目の実績の明細No　＝　1行目の明細のNo
        newListSalesResults[0].salesDetailNo = newListDetails[0].salesDetailNo;
      }
    }

    return {
      ...invoice,
      listSalesDetailInfo: newListDetails,
      listSalesResultsInfo: newListSalesResults,
    };
  }

  function filterInvoice(invoice) {
    const currentUserId = getLoginUserInfo().userId;
    let result = {};
    result.salesRecordInfo = {
      ...invoice.salesRecord.Value,
      createdId: currentUserId,
      updatedId: currentUserId,
      updateHistoryProps:
        invoice.historyItems.Value
          .filter((item) => item.itemName.startsWith('header'))
          .map((item) => ({property: item.itemName.split("-")[2], value: item.value, valueName: item.valueName}))
    };
    result.listSalesDetailInfo = filterSalesDetails(invoice);
    result.listSalesResultsInfo = filterSalesResults(invoice);
    return result;
  }

  function filterSalesDetails(invoice) {
    const currentUserId = getLoginUserInfo().userId;
    let filteredSalesDetails = [];
    filteredSalesDetails = invoice.salesDetails.Value
      .map((detail, index) => ({
        ...detail,
        updateHistoryProps:
          invoice.historyItems.Value
            .filter((item) => item.itemName.startsWith(`detail-${index}`))
            .map((item) => ({property: item.itemName.split("-")[2], value: item.value, valueName: item.valueName}))
      }))
      .filter(
        (detail) => !(detailIsEmpty(detail) && detail.isNew)
      )
      .map((detail) => ({
        ...detail,
        salesRecordNo : invoice.salesRecord.Value.salesRecordNo,
        isRemove: detailIsEmpty(detail),
        purchacePrice: detail.purchacePrice || 0,
        createdId: currentUserId,
        updatedId: currentUserId,
      }));
    return filteredSalesDetails;
  }

  function filterSalesResults(invoice) {
    const isNewInvoice = invoice.salesRecord.Value.isNewInvoice;
    let filteredSalesResults = [];
    const currentUserId = getLoginUserInfo().userId;

    filteredSalesResults = invoice.salesResultses.Value
      .map((result, index) => ({
        ...result,
        updateHistoryProps:
          invoice.historyItems.Value
            .filter((item) => item.itemName.startsWith(`result-${index}`))
            .map((item) => ({property: item.itemName.split("-")[2], value: item.value, valueName: item.valueName}))
      }))
      .filter(
        (result) => !(resultsIsEmpty(result) && result.isNew)
      ).map((result, index) => ({
        ...result,
        salesRecordNo : invoice.salesRecord.Value.salesRecordNo,
        isRemove: resultsIsEmpty(result),
        salesDetailId:
          invoice.salesDetails.Value.find(
            (detail) => detail.salesDetailNo == result.salesDetailNo
          )?.salesDetailId || null,
        containerQuantity: parseInt(result.containerQuantity) || 0,
        createdId: currentUserId,
        updatedId: currentUserId,
      }));

    return filteredSalesResults;
  }

  async function updateSalesRecord(newInvoice, onUpdated = () => {}) {
    try {
      // console.log("登録原票 Before Filter：", newInvoice);
      const filteredInvoice = filterInvoice(newInvoice);
      if (!checkValidInvoice(filteredInvoice)) {
        return;
      }
      console.log("登録原票：", filteredInvoice);

      await upsertSalesRecord(filteredInvoice);

      insertedInvoice.setValue(filteredInvoice);
      setupNewSalesRecord({
        salesRecordTypeCd: filteredInvoice.salesRecordInfo.salesRecordTypeCd,
        arrivalDate: filteredInvoice.salesRecordInfo.isNewInvoice
          ? filteredInvoice.salesRecordInfo.arrivalDate
          : null,
        deliveryDate: filteredInvoice.salesRecordInfo.isNewInvoice
          ? filteredInvoice.salesRecordInfo.deliveryDate
          : null,
      });
      
      onUpdated();

      document.getElementById(`header-2`).focus();
    } catch (e) {
      alert(e);
    }
  }

  async function updateStock(newInvoice, onUpdated = () => {}) {
    try {
      const filteredInvoice = filterInvoice(newInvoice);
      if (!checkValidInvoice(filteredInvoice)) {
        return;
      }

      const submitData = {
        salesRecordInfo: filteredInvoice.salesRecordInfo,
        listSalesResultsInfo: filteredInvoice.listSalesResultsInfo,
      };

      await upsertStock(submitData);
      setupNewSalesRecord({ salesRecordTypeCd: SALES_RECORD_TYPE.STOCK });
      
      onUpdated();

      document.getElementById(`header-2`).focus();
    } catch (e) {
      alert(e);
    }
  }

  function checkValidInvoice(invoiceData) {
    const errs = validateSalesRecord(invoiceData);
    if (errs.length > 0) {
      console.log(errs);
      alert(errs.join("\n"));
      return false;
    }
    return true;
  }

  function setupNewSalesRecord({
    salesRecordTypeCd = SALES_RECORD_TYPE.LOCAL,
    arrivalDate,
    deliveryDate,
  }) {
    const newSalesRecord = generateNewSalesRecord({
      salesRecordTypeCd,
      arrivalDate,
      deliveryDate,
    });
    invoice.setValue(newSalesRecord);
  }
  return {
    isLoading,
    invoice: invoice,
    insertedInvoice,
    getSalesRecordByNo,
    setupNewSalesRecord,
    updateSalesRecord,
    updateStock,
    renderState,
  };
};

export default useSalesRecordAPI;
